const WRITE_ROUTES: Record<string, string> = {
  '/api/update_account': 'update_account',
  '/api/add_address': 'add_address',
  '/api/add-address': 'add_address',
  '/.netlify/functions/add-address': 'add_address',
};
const ENDPOINT_WRITE_QUEUES: Record<string, string> = {
  resend_verification_email: 'POST', remove_order: 'DELETE',
  update_liked_items: 'POST', save_3dlook_scan_inputs: 'POST', webhook_3dlook: 'POST',
  manage_messages: 'POST', upload_user_photo: 'POST', save_user_ai_photo: 'POST',
  try_on_credit_check: 'POST', try_on_save_result: 'POST', delete_fabric: 'DELETE',
};
const PRIVATE_RECEIPT_QUEUES = new Set(Object.keys(ENDPOINT_WRITE_QUEUES));
const PASSWORD_RESET_QUEUES = new Set(['send_password_reset_email', 'change_password']);

export function getAccountWriteQueue(path: string, init: RequestInit) {
  const pathname = new URL(path, 'http://localhost').pathname;
  const method = (init.method ?? 'GET').toUpperCase();
  const endpointQueue = pathname.split('/').pop()?.replaceAll('-', '_') ?? '';
  if (ENDPOINT_WRITE_QUEUES[endpointQueue] === method) return endpointQueue;
  const passwordResetQueue = pathname.split('/').pop() ?? '';
  if (pathname.startsWith('/api/') && PASSWORD_RESET_QUEUES.has(passwordResetQueue)) {
    return method === 'POST' ? passwordResetQueue : undefined;
  }
  if (['/api/user_raw_data', '/api/user-raw-data', '/.netlify/functions/user_raw_data'].includes(pathname)) {
    return method === 'POST' ? 'user_raw_data' : undefined;
  }
  if (['/api/manage_user_ai_data', '/api/manage-user-ai-data', '/.netlify/functions/manage-user-ai-data'].includes(pathname)) {
    return ['POST', 'DELETE'].includes(method) ? 'manage_user_ai_data' : undefined;
  }
  if (pathname === '/api/manage_notifications') {
    return (init.method ?? 'GET').toUpperCase() === 'PUT' ? 'manage_notifications' : undefined;
  }
  return ['POST', 'PUT'].includes((init.method ?? 'GET').toUpperCase())
    ? WRITE_ROUTES[pathname]
    : undefined;
}

// Wait for the worker's receipt so callers still receive saved data and validation errors.
export async function fetchQueuedAccountWrite(
  queue: string,
  init: RequestInit,
  request: (path: string, init: RequestInit) => Promise<Response>,
  query?: Record<string, string | undefined>,
  requestId = crypto.randomUUID(),
) {
  const body: unknown = JSON.parse(typeof init.body === 'string' ? init.body : '{}');
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    throw new Error('Queued updates require a JSON object.');
  }
  const headers = new Headers(init.headers);
  headers.set('Content-Type', 'application/json');
  const sessionId = (body as Record<string, unknown>).session_id;
  if (queue === 'user_raw_data' && typeof sessionId === 'string' && !headers.has('Authorization') && !headers.has('X-Session-ID')) {
    headers.set('X-Session-ID', sessionId);
  }
  const accepted = await request('/api/enqueue_tasks', {
    ...init,
    method: 'POST',
    headers,
    body: JSON.stringify({
      ...(PRIVATE_RECEIPT_QUEUES.has(queue) ? query : {}),
      ...body,
      ...(query && !PRIVATE_RECEIPT_QUEUES.has(queue) ? { query } : {}),
      queue, method: init.method?.toUpperCase(), request_id: requestId,
    }),
  });
  if (!accepted.ok || accepted.status !== 202) return accepted;

  const receipt: { request_id?: string; receipt_token?: string } = await accepted.clone().json();
  if (!receipt.request_id) throw new Error('The queue did not return a request receipt.');
  const receiptHeaders = new Headers(headers);
  if (PASSWORD_RESET_QUEUES.has(queue) || PRIVATE_RECEIPT_QUEUES.has(queue)) {
    if (!receipt.receipt_token) throw new Error('The queue did not return a private receipt token.');
    receiptHeaders.set('X-Queue-Receipt-Token', receipt.receipt_token);
  }
  const statusPath = `/api/enqueue_tasks?queue=${queue}&request_id=${encodeURIComponent(receipt.request_id)}`;
  const attempts = queue === 'manage_user_ai_data' ? 240 : 60;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 1000));
    const result = await request(statusPath, { method: 'GET', headers: receiptHeaders, signal: init.signal, cache: 'no-store' });
    if (result.status !== 202) return result;
  }
  return new Response(JSON.stringify({
    message: PASSWORD_RESET_QUEUES.has(queue)
      ? 'Your password reset is still queued. Wait before trying again, and check your email or try signing in.'
      : 'Your update is still queued. Refresh later to check the saved result before submitting again.',
    request_id: receipt.request_id,
  }), { status: 504, headers: { 'Content-Type': 'application/json' } });
}
